(() => {
  if (customElements.get('byards-welcome')) return;
  const DISMISSED = 'byards_welcome_dismissed';
  const SUBSCRIBED = 'byards_welcome_subscribed';
  const PENDING = 'byards_welcome_pending';
  const hasCookie = (name) => document.cookie.split(';').some((value) => value.trim().startsWith(`${name}=`));
  const remember = (name, days) => {
    document.cookie = `${name}=1; Max-Age=${Math.round(days * 86400)}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
  };

  class ByardsWelcome extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') return;
      this.designMode = this.dataset.designMode === 'true';
      this.events = new AbortController();
      const { signal } = this.events;
      this.querySelectorAll('[data-bw-close]').forEach((button) => button.addEventListener('click', () => this.close(), { signal }));
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('bw-welcome-open');
        if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => this.trapTab(event), { signal });
      this.form.addEventListener('submit', (event) => {
        if (this.designMode) {
          event.preventDefault();
          this.querySelector('[data-bw-preview-note]')?.focus();
          return;
        }
        // Preserve Shopify's native submission, validation and challenge flow.
        // The marker scopes the returned server result to this particular form.
        try { sessionStorage.setItem(PENDING, JSON.stringify({ form: this.form.id, time: Date.now() })); } catch (_) { /* Storage may be unavailable. */ }
      }, { signal });

      if (this.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open(true);
        }, { signal });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
        }, { signal });
        return;
      }
      if (this.dataset.enabled !== 'true') return;

      let pending = false;
      try {
        const stored = JSON.parse(sessionStorage.getItem(PENDING) || 'null');
        pending = stored?.form === this.form.id && Date.now() - stored.time < 30 * 60 * 1000;
      } catch (_) { /* Continue without session storage. */ }
      const ownAnchor = location.hash === `#${this.form.id}`;
      const success = this.querySelector('[data-bw-success]');
      const error = this.querySelector('[data-bw-error]');
      if ((pending || (ownAnchor && !hasCookie(SUBSCRIBED))) && (success || error)) {
        try { sessionStorage.removeItem(PENDING); } catch (_) { /* No stored email or personal data. */ }
        if (success) remember(SUBSCRIBED, 365);
        this.resultElement = error || success;
        this.showResult();
        return;
      }
      // Never infer success from URL parameters or the submit event.
      if (success || error || hasCookie(SUBSCRIBED) || hasCookie(DISMISSED)) return;
      this.readyAt = Date.now() + Math.max(8, Number(this.dataset.delay) || 8) * 1000;
      const engage = () => { this.engaged = true; this.attemptOpen(); };
      ['click', 'keydown', 'scroll'].forEach((name) => document.addEventListener(name, engage, { signal, passive: true, once: true }));
      document.addEventListener('visibilitychange', () => this.attemptOpen(), { signal });
      this.timer = window.setTimeout(() => this.attemptOpen(), this.readyAt - Date.now());
    }

    hasBlockingUI() {
      if (document.hidden) return true;
      if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return true;
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"], pop-up[open], cart-drawer[open], .drawer[open], #shopify-pc__banner')].some((element) => {
        if (this.contains(element)) return false;
        const style = getComputedStyle(element);
        return element.getClientRects().length && style.visibility !== 'hidden' && style.display !== 'none';
      });
    }

    attemptOpen() {
      window.clearTimeout(this.timer);
      if (this.opened || !this.engaged || hasCookie(SUBSCRIBED) || hasCookie(DISMISSED)) return;
      if (Date.now() < this.readyAt) { this.timer = window.setTimeout(() => this.attemptOpen(), this.readyAt - Date.now()); return; }
      if (this.hasBlockingUI()) { this.timer = window.setTimeout(() => this.attemptOpen(), 1000); return; }
      this.open();
    }

    showResult() {
      if (this.hasBlockingUI()) { this.timer = window.setTimeout(() => this.showResult(), 500); return; }
      this.open();
      this.resultElement.focus({ preventScroll: true });
    }

    open(preview = false) {
      if (this.dialog.open || (!preview && this.hasBlockingUI())) return;
      this.previousFocus = document.activeElement;
      this.opened = true;
      this.dialog.showModal();
      document.documentElement.classList.add('bw-welcome-open');
      // Focus close rather than email so opening never launches a mobile keyboard.
      this.querySelector('[data-bw-close]').focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('byards:welcome:open', { bubbles: true }));
    }

    trapTab(event) {
      if (event.key !== 'Tab') return;
      const nodes = [...this.dialog.querySelectorAll('button:not([disabled]), input:not([type="hidden"]):not([disabled]), a[href], [tabindex="0"]')].filter((node) => node.getClientRects().length);
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    close(persist = true) {
      if (!this.dialog.open) return;
      if (persist && !this.designMode) remember(DISMISSED, Math.max(1, Number(this.dataset.dismissDays) || 7));
      this.dialog.close();
    }

    disconnectedCallback() {
      this.close(false);
      this.events?.abort();
      window.clearTimeout(this.timer);
    }
  }
  customElements.define('byards-welcome', ByardsWelcome);
})();
